'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

interface BreadcrumbProps {
  className?: string;
  customBreadcrumbs?: Array<{ label: string; href: string }>;
}

interface BreadcrumbItem {
  label: string;
  href: string;
}

const Breadcrumb = ({ className = '', customBreadcrumbs }: BreadcrumbProps) => {
  const pathname = usePathname();

  const routeLabels: Record<string, string> = {
    '/home': 'Home',
    '/about-us': 'About Us',
    '/product-details': 'Products',
    '/shopping-cart': 'Shopping Cart',
    '/order-confirmation': 'Order Confirmation',
    '/customer-profile': 'My Account',
  };

  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    const paths = pathname.split('/').filter(Boolean);
    const breadcrumbs: BreadcrumbItem[] = [{ label: 'Home', href: '/home' }];

    let currentPath = '';
    paths.forEach((path) => {
      currentPath += `/${path}`;
      const label = routeLabels[currentPath] || path.charAt(0).toUpperCase() + path.slice(1);
      breadcrumbs.push({ label, href: currentPath });
    });

    return breadcrumbs;
  };
  
  const breadcrumbs = customBreadcrumbs && customBreadcrumbs.length > 0
    ? customBreadcrumbs
    : generateBreadcrumbs();

  if (!breadcrumbs || breadcrumbs.length <= 1) {
    return null;
  }

  return (
    <nav data-component-id="src/components/common/Breadcrumb.tsx:52:4" data-component-path="src/components/common/Breadcrumb.tsx" data-component-line="52" data-component-end-line="85" data-component-file="Breadcrumb.tsx" data-component-name="nav" data-component-content="%7B%22elementName%22%3A%22nav%22%7D"
      aria-label="Breadcrumb"
      className={`flex items-center gap-2 text-sm ${className}`}
    >
      <ol data-component-id="src/components/common/Breadcrumb.tsx:56:6" data-component-path="src/components/common/Breadcrumb.tsx" data-component-line="56" data-component-end-line="84" data-component-file="Breadcrumb.tsx" data-component-name="ol" data-component-content="%7B%22elementName%22%3A%22ol%22%2C%22className%22%3A%22flex%20items-center%20gap-2%20flex-wrap%22%7D" className="flex items-center gap-2 flex-wrap">
        {breadcrumbs.map((crumb, index) => {
          const isLast = index === breadcrumbs.length - 1;

          return (
            <li data-component-id="src/components/common/Breadcrumb.tsx:61:12" data-component-path="src/components/common/Breadcrumb.tsx" data-component-line="61" data-component-end-line="81" data-component-file="Breadcrumb.tsx" data-component-name="li" data-component-content="%7B%22elementName%22%3A%22li%22%2C%22className%22%3A%22flex%20items-center%20gap-2%22%7D" key={crumb.href} className="flex items-center gap-2">
              {index > 0 && (
                <Icon data-component-id="src/components/common/Breadcrumb.tsx:63:16" data-component-path="src/components/common/Breadcrumb.tsx" data-component-line="63" data-component-end-line="67" data-component-file="Breadcrumb.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ChevronRightIcon%22%2C%22className%22%3A%22text-muted-foreground%22%7D"
                  name="ChevronRightIcon"
                  size={16}
                  className="text-muted-foreground"
                />
              )}
              {isLast ? (
                <span data-component-id="src/components/common/Breadcrumb.tsx:70:16" data-component-path="src/components/common/Breadcrumb.tsx" data-component-line="70" data-component-end-line="72" data-component-file="Breadcrumb.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-medium%20text-foreground%22%7D" className="font-medium text-foreground" aria-current="page">
                  {crumb.label}
                </span>
              ) : (
                <Link data-component-id="src/components/common/Breadcrumb.tsx:74:16" data-component-path="src/components/common/Breadcrumb.tsx" data-component-line="74" data-component-end-line="79" data-component-file="Breadcrumb.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22className%22%3A%22text-muted-foreground%20hover%3Atext-primary%20transition-smooth%20focus%3Aoutline-none%20focus%3Atext-primary%20focus%3Aunderline%22%7D"
                  href={crumb.href}
                  className="text-muted-foreground hover:text-primary transition-smooth focus:outline-none focus:text-primary focus:underline"
                >
                  {crumb.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumb;